/**
 * Statsig Manager
 *
 * Handles Statsig feature flag and experiment service.
 * This manager provides:
 * - Statsig SDK initialization (web and native)
 * - User identity sync for feature targeting
 * - Feature flag checks
 * - Dynamic configuration
 * - Experiment segment checks
 * - State reset on logout
 */

import { StatsigClientExpo } from '@statsig/expo-bindings';
import { StatsigClient } from '@statsig/js-client';
import { jwtDecode } from 'jwt-decode';
import DeviceInfo from 'react-native-device-info';
import { getAnonymousId } from 'services/analytics/segment/segment';
import { tier } from 'services/statsig/helpers';
import { isWeb } from 'shared/constants/platform';
import { STORAGE_KEYS } from 'shared/constants/storage';
import { Environment } from 'shared/providers/environment.constants';
import {
  DynamicConfigurationKey,
  ExperimentKey,
  FeatureFlagKey,
} from 'shared/providers/statsig/StatsigProvider.d';
import { defaultAppVersion } from 'shared/utils/appVersionUtils';
import { getItem } from 'shared/utils/asyncStorage';
import getEnvironmentVar from 'shared/utils/getEnvironmentVar';
import { logBreadcrumb, logError } from 'shared/utils/sentry/sentry';

let statsigClient: StatsigClient | StatsigClientExpo | null = null;

/**
 * Initialize Statsig with user context
 * If user is already authenticated, initializes with userID
 * Otherwise initializes as anonymous
 *
 * @param currentEnv - Current environment (dev, prod, etc.)
 */
export async function initializeStatsig(
  currentEnv: Environment,
): Promise<void> {
  try {
    logBreadcrumb({
      level: 'info',
      message: '[DEBUG] initializeStatsig: starting',
    });

    const sdkKey = isWeb
      ? getEnvironmentVar('EXPO_STATSIG_WEB_API_KEY')
      : getEnvironmentVar('EXPO_STATSIG_NATIVE_API_KEY');

    logBreadcrumb({
      level: 'info',
      message: '[DEBUG] initializeStatsig: got sdkKey, calling getAnonymousId',
    });

    // Get anonymousId from Segment (already initialized in previous phase)
    const anonymousId = (await getAnonymousId()) ?? 'anonymous';

    logBreadcrumb({
      level: 'info',
      message: '[DEBUG] initializeStatsig: got anonymousId',
      data: { anonymousId },
    });

    // Get tier helper
    const envTier = tier(currentEnv);

    logBreadcrumb({
      level: 'info',
      message: '[DEBUG] initializeStatsig: checking for existing token',
    });

    // Check if user is already authenticated (session exists)
    let userId: string | undefined;
    try {
      const existingToken = await getItem(STORAGE_KEYS.ID_TOKEN);
      if (existingToken) {
        const decoded = jwtDecode<{ 'custom:user_id': string }>(
          existingToken as string,
        );
        userId = decoded['custom:user_id'];
      }
    } catch {
      // No existing session or invalid token - will initialize as anonymous
    }

    logBreadcrumb({
      level: 'info',
      message: '[DEBUG] initializeStatsig: creating StatsigClient',
      data: { isWeb, hasUserId: !!userId },
    });

    if (isWeb) {
      statsigClient = new StatsigClient(
        sdkKey,
        {
          ...(userId ? { userID: userId } : {}),
          customIDs: { stableID: anonymousId },
          appVersion: defaultAppVersion,
          custom: { platform: 'web' },
        },
        { environment: { tier: envTier } },
      );

      logBreadcrumb({
        level: 'info',
        message: '[DEBUG] initializeStatsig: calling initializeAsync (web)',
      });

      await statsigClient.initializeAsync({ timeoutMs: 10000 });

      logBreadcrumb({
        level: 'info',
        message: '[DEBUG] initializeStatsig: initializeAsync completed (web)',
      });
    } else {
      statsigClient = new StatsigClientExpo(
        sdkKey,
        {
          ...(userId ? { userID: userId } : {}),
          customIDs: { stableID: anonymousId },
          appVersion: DeviceInfo.getVersion(),
          custom: { platform: DeviceInfo.getSystemName() },
        },
        { environment: { tier: envTier } },
      );

      logBreadcrumb({
        level: 'info',
        message: '[DEBUG] initializeStatsig: calling initializeAsync (native)',
      });

      await statsigClient.initializeAsync({ timeoutMs: 10000 });

      logBreadcrumb({
        level: 'info',
        message:
          '[DEBUG] initializeStatsig: initializeAsync completed (native)',
      });
    }

    if (!statsigClient) {
      logBreadcrumb({
        level: 'error',
        message: 'Statsig client not initialized properly',
      });
    }

    logBreadcrumb({
      level: 'info',
      message: 'Statsig initialized successfully',
      data: {
        hasUserId: !!userId,
        anonymousId,
        environment: envTier,
      },
    });
  } catch (error) {
    logError(error);
    logBreadcrumb({
      level: 'error',
      message: 'Failed to initialize Statsig',
      data: { error },
    });
  }
}

/**
 * Update Statsig user after login
 *
 * @param userId - User ID from authentication
 * @param anonymousId - Anonymous ID from Segment for stable identification
 */
export async function updateStatsigUser(
  userId: string,
  anonymousId: string,
): Promise<void> {
  if (!statsigClient) {
    throw new Error('Statsig not initialized');
  }

  try {
    const userProperties = {
      userID: userId,
      customIDs: { stableID: anonymousId },
      ...(isWeb
        ? { appVersion: defaultAppVersion, custom: { platform: 'web' } }
        : {
            appVersion: DeviceInfo.getVersion(),
            custom: { platform: DeviceInfo.getSystemName() },
          }),
    };

    await statsigClient.updateUserAsync(userProperties);

    logBreadcrumb({
      level: 'info',
      message: 'Statsig user updated',
      data: { userId },
    });
  } catch (error) {
    logError(error);
    logBreadcrumb({
      level: 'error',
      message: 'Failed to update Statsig user',
      data: { error },
    });
    throw error;
  }
}

export async function updateStatsigCustomIDs(
  customIDs: Record<string, string>,
): Promise<void> {
  if (!statsigClient) {
    throw new Error('Statsig not initialized');
  }

  await statsigClient.updateUserAsync({ customIDs });
}

/**
 * Reset Statsig on logout
 * Returns to the same state as app initialization (anonymous user with stableID)
 */
export async function resetStatsig(): Promise<void> {
  if (!statsigClient) {
    return;
  }

  try {
    // Get anonymousId from Segment (same as initialization)
    const anonymousId = (await getAnonymousId()) ?? 'anonymous';

    // Reset to the same anonymous state as initialization: only customIDs.stableID,
    // no userID.
    const userProperties = {
      customIDs: { stableID: anonymousId },
      ...(isWeb
        ? { appVersion: defaultAppVersion, custom: { platform: 'web' } }
        : {
            appVersion: DeviceInfo.getVersion(),
            custom: { platform: DeviceInfo.getSystemName() },
          }),
    };

    await statsigClient.updateUserAsync(userProperties);

    logBreadcrumb({
      level: 'info',
      message: 'Statsig reset to anonymous state',
      data: { anonymousId },
    });
  } catch (error) {
    logError(error);
    logBreadcrumb({
      level: 'error',
      message: 'Failed to reset Statsig',
      data: { error },
    });
  }
}

/**
 * Check if a feature flag is enabled
 *
 * @param key - Feature flag key to check
 * @returns True if feature is enabled, false otherwise
 */
export function isFeatureEnabled(key: FeatureFlagKey): boolean {
  if (!statsigClient) {
    logError('Statsig not initialized, returning false for feature flag');
    return false;
  }

  return statsigClient.checkGate(key);
}

/**
 * Get dynamic configuration from Statsig
 *
 * @param key - Dynamic configuration key
 * @returns Configuration value object
 */
export function getDynamicConfiguration(
  key: DynamicConfigurationKey,
): Record<string, unknown> {
  if (!statsigClient) {
    logError('Statsig not initialized, returning empty config');
    return {};
  }
  const configuration = statsigClient.getDynamicConfig(key);
  return configuration.value;
}

/**
 * Check if an experiment segment is active
 *
 * @param key - Experiment key
 * @param segment - Segment name to check
 * @returns True if the segment is active for the current user
 */
export function experimentSegmentActive(
  key: ExperimentKey,
  segment: string,
): boolean {
  if (!statsigClient) {
    logError('Statsig not initialized, returning false for experiment');
    return false;
  }
  const experiment = statsigClient.getExperiment(key);
  return experiment.groupName === segment;
}

/**
 * Get the Statsig client instance
 *
 * @returns The Statsig client instance or null if not initialized
 */
export function getStatsigClient(): StatsigClient | StatsigClientExpo | null {
  return statsigClient;
}
